import React from 'react';
import { formatCurrency } from '../../utils/formatCurrency';

interface BIResultsCardProps {
  result: {
    final_claim_amount: number;
    revenue_loss: number;
    causation_passed: boolean;
    rtp_multiplier?: number;
  };
  isPeakSeason?: boolean;
  contingencyBufferPercent?: number;
  accountantFees?: number;
  continuingExpenses?: number;
  extraExpenses?: number;
}

export const BIResultsCard: React.FC<BIResultsCardProps> = ({
  result,
  isPeakSeason,
  contingencyBufferPercent,
  accountantFees,
  continuingExpenses,
  extraExpenses,
}) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <h2 className="text-xl font-semibold text-gray-900">BI Calculation Results</h2>
      </div>
      <div className="p-6">
        {/* Peak Season Alert */}
        {isPeakSeason && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <span className="text-amber-500">⚠️</span>
            <span className="text-sm text-amber-800">
              <strong>Peak Season Event:</strong> This claim occurred during a historically high-revenue period.
            </span>
          </div>
        )}
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Final Claim Amount</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatCurrency(result.final_claim_amount || 0)}
            </p>
            {contingencyBufferPercent && contingencyBufferPercent > 0 && (
              <p className="text-xs text-green-600 mt-1">
                Includes {contingencyBufferPercent}% contingency buffer
              </p>
            )}
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Revenue Loss</p>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(result.revenue_loss || 0)}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Causation Test</p>
            <p className="font-semibold">
              {result.causation_passed ? (
                <span className="text-green-600">✓ Passed</span>
              ) : (
                <span className="text-red-600">✗ Failed</span>
              )}
            </p>
          </div>
          {result.rtp_multiplier && result.rtp_multiplier !== 1 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">RTP Multiplier</p>
              <p className="text-xl font-bold text-gray-900">
                {result.rtp_multiplier}x
              </p>
            </div>
          )}
        </div>
        
        {/* Extra Expenses Summary if present */}
        {((accountantFees && accountantFees > 0) || (continuingExpenses && continuingExpenses > 0) || (extraExpenses && extraExpenses > 0)) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Additional Expenses Included:</p>
            <div className="flex flex-wrap gap-3">
              {accountantFees && accountantFees > 0 && (
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                  Professional Fees: {formatCurrency(accountantFees)}
                </span>
              )}
              {continuingExpenses && continuingExpenses > 0 && (
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                  Continuing: {formatCurrency(continuingExpenses)}
                </span>
              )}
              {extraExpenses && extraExpenses > 0 && (
                <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                  Extra: {formatCurrency(extraExpenses)}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BIResultsCard;
